import {
  useMutation as useTanstackMutation,
  type UseMutationResult,
} from "@tanstack/react-query";
import type { CustomMutationOptions, MutationMeta } from "./Props";

export type { CustomMutationOptions } from "./Props";

/**
 * Custom useMutation hook with enhanced error/success handling
 *
 * Features:
 * - passThrough control for global error/success handling
 * - Auto-extracts backend messages
 * - Optional custom message overrides
 * - Optional local callbacks
 *
 * @example
 * ```typescript
 * // Use backend messages
 * const createMutation = useCustomMutation({
 *   mutationFn: createUser,
 *   errorContext: "Creating User",
 *   showSuccessToast: true,
 * });
 *
 * // Override with custom messages
 * const createMutation = useCustomMutation({
 *   mutationFn: createUser,
 *   customErrorMessage: "Failed to create user account",
 *   customSuccessMessage: "User account created successfully!",
 *   successContext: "Create User",
 * });
 *
 * // Handle locally only
 * const createMutation = useCustomMutation({
 *   mutationFn: createUser,
 *   passThrough: false,
 *   onSuccess: (data) => navigate(`/users/${data.id}`),
 *   onError: (error) => setFormError(error.message),
 * });
 * ```
 */
export function useCustomMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext extends { meta?: MutationMeta } = { meta?: MutationMeta },
>(
  options: CustomMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const {
    passThrough = true,
    navigateOnError = true,
    withoutToastMessage = false,
    errorContext,
    customErrorMessage,
    successContext,
    customSuccessMessage,
    showSuccessToast = false,
    onSuccess: localOnSuccess,
    onError: localOnError,
    ...mutationOptions
  } = options;

  return useTanstackMutation({
    ...mutationOptions,
    meta: {
      passThrough,
      navigateOnError,
      withoutToastMessage,
      errorContext,
      customErrorMessage,
      successContext,
      customSuccessMessage,
      showSuccessToast,
    },
    onSuccess: (data, variables, context: TContext) => {
      // Attach meta to context for provider access. `context` is undefined
      // when the mutation has no `onMutate`.
      const contextWithMeta = {
        ...(context || {}),
        meta: {
          ...(context?.meta || {}),
          passThrough,
          navigateOnError,
          withoutToastMessage,
          successContext,
          customSuccessMessage,
          showSuccessToast,
        },
      } as TContext;

      // Call local callback if provided
      if (localOnSuccess) {
        localOnSuccess(data, variables, contextWithMeta);
      }
    },
    onError: (error, variables, context) => {
      // Attach meta to context for provider access
      const contextWithMeta = {
        ...(context || {}),
        meta: {
          ...(context?.meta || {}),
          passThrough,
          navigateOnError,
          withoutToastMessage,
          errorContext,
          customErrorMessage,
        },
      } as TContext;

      // Call local callback if provided
      if (localOnError) {
        localOnError(error, variables, contextWithMeta);
      }
    },
  });
}
